import React, { useEffect, useState } from 'react';
import './i18n';
import { useTranslation } from 'react-i18next';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';

import { useAppCtx } from './app-ctx';
import VerificationModal from './verification-modal';

/**
 * My Profile — editable name/email/phone fields with a single Apply button.
 *
 * When phone and/or email are changed, the backend sends SMS/email
 * verifications and the reusable VerificationModal collects the codes.
 */
const MyProfile = () => {
  const { t } = useTranslation();
  const { JOOMLA_VARS, CONFIG } = useAppCtx();

  const appRoot = JOOMLA_VARS.root;
  const csrfToken = JOOMLA_VARS.csrfToken || '';

  const [profile, setProfile] = useState({ name: '', phone: '', email: '' });
  const [flags, setFlags] = useState({
    phoneValidationAvailable: false,
    smsResendDelay: 120,
    emailResendDelay: 120,
  });
  const [loaded, setLoaded] = useState(false);

  // Editable field states
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');

  // Reusable verification popup state
  const [modal, setModal] = useState({
    open: false,
    needsPhone: false,
    needsEmail: false,
    maskedPhone: '',
    maskedEmail: '',
    smsResendDelay: 120,
    emailResendDelay: 120,
    error: '',
  });

  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(false);

  // Incremented whenever the popup countdowns must restart (apply or resend).
  const [modalNonce, setModalNonce] = useState(0);

  useEffect(() => {
    // Abort on unmount so the response never sets state on a dead component
    const controller = new AbortController();
    fetch(appRoot + CONFIG.getProfile, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
      body: JSON.stringify({}),
      signal: controller.signal,
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.error) return;
        setProfile({
          name: data.name || '',
          phone: data.phone || '',
          email: data.email || '',
        });
        setFlags({
          phoneValidationAvailable: !!data.phoneValidationAvailable,
          smsResendDelay: data.smsResendDelay || 120,
          emailResendDelay: data.emailResendDelay || 120,
        });
        setName(data.name || '');
        setPhone(data.phone || '');
        setEmail(data.email || '');
        setLoaded(true);
      })
      .catch((e) => {
        // Ignore cancellations caused by cleanup (unmount)
        if (e && e.name === 'AbortError') 
          return;
        console.warn('MyProfile: failed to load profile', e);
      });

    return () => {
      controller.abort();
    };
  }, []);

  const post = (config, payload) =>
    fetch(appRoot + config, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken,
      },
      body: JSON.stringify(payload),
    }).then((r) => r.json());

  const closeModal = () =>
    setModal({
      open: false,
      needsPhone: false,
      needsEmail: false,
      maskedPhone: '',
      maskedEmail: '',
      smsResendDelay: 120,
      emailResendDelay: 120,
      error: '',
    });

  const clearError = (field) =>
    setErrors((current) => {
      if (!Object.prototype.hasOwnProperty.call(current, field)) return current;
      const next = { ...current };
      delete next[field];
      return next;
    });

  const handleNameChange = (value) => {
    setName(value);
    clearError('name');
  };

  const handleEmailChange = (value) => {
    setEmail(value);
    clearError('email');
  };

  const handlePhoneChange = (value) => {
    setPhone(value);
    clearError('phone');
  };

  // Revert unsaved form fields back to the last applied profile values.
  const handleReset = () => {
    setName(profile.name);
    setPhone(profile.phone);
    setEmail(profile.email);
    setErrors({});
    setSuccess(false);
  };

  const handleApply = async () => {
    setErrors({});
    setSuccess(false);
    setSaving(true);
    try {
      const data = await post(CONFIG.applyProfile, { name, phone, email });

      if (data.status === 'saved') {
        setProfile({ name, phone, email });
        setSuccess(true);
      } else if (data.status === 'verification_required') {
        setModalNonce((n) => n + 1);
        setModal({
          open: true,
          needsPhone: !!data.needsPhone,
          needsEmail: !!data.needsEmail,
          maskedPhone: data.maskedPhone || '',
          maskedEmail: data.maskedEmail || '',
          smsResendDelay: data.smsResendDelay || flags.smsResendDelay,
          emailResendDelay: data.emailResendDelay || flags.emailResendDelay,
          error: '',
        });
      } else {
        // Show each field's validation error inline (below the field).
        setErrors(data.errors || {});
      }
    } finally {
      setSaving(false);
    }
  };

  const handleResend = async (scope) => {
    const data = await post(CONFIG.resendVerification, { scope });
    if (data.status === 'sent') {
      setModalNonce((n) => n + 1);
      setModal((m) => ({
        ...m,
        smsResendDelay: scope === 'phone' ? data.resendDelay || m.smsResendDelay : m.smsResendDelay,
        emailResendDelay: scope === 'email' ? data.resendDelay || m.emailResendDelay : m.emailResendDelay,
        error: '',
      }));
    } else {
      setModal((m) => ({
        ...m,
        error: data.errors?.phone || data.errors?.email || t('Resend failed'),
      }));
    }
  };

  const handleConfirm = async (phoneCode, emailCode) => {
    const data = await post(CONFIG.verifyProfile, { phone, phoneCode, email, emailCode });
    if (data.status === 'verified') {
      setProfile({ name, phone, email });
      closeModal();
      setSuccess(true);
    } else {
      setModal((m) => ({
        ...m,
        error:
          data.errors?.code || data.errors?.phone || data.errors?.email || t('Invalid verification code'),
      }));
    }
  };

  if (!loaded) {
    return <div className="loading">{t('Loading ...')}</div>;
  }

  return (
    <div className="kurortlife-profile">
      <h2>{t('Edit profile')}</h2>

      {success && (
        <div className="profile-success" role="status">
          <span className="profile-success-message">{t('Profile changed successfully')}</span>
          <button
            type="button"
            className="profile-success-close"
            onClick={() => setSuccess(false)}
            aria-label={t('Close')}
          >
            &times;
          </button>
        </div>
      )}

      <div className={`profile-field${errors.name ? ' has-error' : ''}`}>
        <label>{t('Your name')}</label>
        <input type="text" value={name} onChange={(e) => handleNameChange(e.target.value)} />
        {errors.name && <div className="error-text">{errors.name}</div>}
      </div>

      <div className={`profile-field${errors.email ? ' has-error' : ''}`}>
        <label>{t('Email')}</label>
        <input type="email" value={email} onChange={(e) => handleEmailChange(e.target.value)} />
        {errors.email && <div className="error-text">{errors.email}</div>}
      </div>

      <div className={`profile-field${errors.phone ? ' has-error' : ''}`}>
        <label>{t('Phone')}</label>
        <div className={`phone-input-wrapper ${errors.phone ? 'error' : ''}`}>
          <PhoneInput
            international
            value={phone}
            onChange={handlePhoneChange}
            placeholder="+380 XX XXX XXXX"
          />
        </div>
        {errors.phone && <div className="error-text">{errors.phone}</div>}
      </div>

      <div className="profile-actions">
        <button
          type="button"
          className="button is-outline"
          onClick={handleReset}
          disabled={saving}
        >
          {t('Reset')}
        </button>
        <button
          type="button"
          className="button is-primary"
          onClick={handleApply}
          disabled={saving}
        >
          {t('Apply')}
        </button>
      </div>

      <VerificationModal
        open={modal.open}
        needsPhone={modal.needsPhone}
        needsEmail={modal.needsEmail}
        maskedPhone={modal.maskedPhone}
        maskedEmail={modal.maskedEmail}
        smsResendDelay={modal.smsResendDelay}
        emailResendDelay={modal.emailResendDelay}
        nonce={modalNonce}
        error={modal.error}
        onResend={handleResend}
        onCancel={closeModal}
        onConfirm={handleConfirm}
      />
    </div>
  );
};

export default MyProfile;